<template>
  <div class="compare-view">
    <div class="container">
      <!-- 区域间对比模块 -->
      <div class="card comparison-card">
        <div class="card-header">
          <i class="fas fa-map-marked-alt"></i>
          <h3>区域间对比</h3>
        </div>
        <div class="card-body">
          <div class="filter-section">
            <!-- 第一行 -->
            <div class="filter-row">
              <!-- 情景选择 -->
              <div class="form-group">
                <select v-model="regionCompare.selectedScenario" class="form-select">
                  <option value="">选择情景</option>
                  <option value="baseline">碳中和情景</option>
                  <option v-for="scenario in customScenarios" 
                          :key="scenario"
                          :value="scenario">
                    {{ scenario }}
                  </option>
                </select>
              </div>

              <!-- 变量选择 -->
              <div class="form-group">
                <select v-model="regionCompare.selectedVariable" 
                        class="form-select"
                        :disabled="regionCompare.selectedSector === 'all'">
                  <option value="">选择变量</option>
                  <option value="gdp">GDP</option>
                  <option value="pop">人口</option>
                  <option value="intensity" 
                          :disabled="regionCompare.selectedSector === 'all'">
                    能源强度
                  </option>
                  <option value="electrification" 
                          :disabled="regionCompare.selectedSector === 'all'">
                    电气化率
                  </option>
                  <option value="hydrogenation" 
                          :disabled="regionCompare.selectedSector === 'all'">
                    氢能化率
                  </option>
                  <option value="energy">终端用能</option>
                </select>
              </div>

              <!-- 变量选择后的部门选择框 -->
              <div class="form-group" v-if="showSectorSelect">
                <select v-model="regionCompare.selectedSector" class="form-select">
                  <option v-for="option in sectorOptions"
                          :key="option.value"
                          :value="option.value"
                          :disabled="option.value === ''">
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <!-- 图表类型选择 -->
              <div class="form-group">
                <select v-model="regionCompare.chartType" 
                        class="form-select"
                        :disabled="regionCompare.selectedVariable === 'energy'">
                  <option value="line" 
                          :disabled="regionCompare.selectedVariable === 'energy'">
                    折线图
                  </option>
                  <option value="bar">柱状图</option>
                </select>
              </div>
            </div>

            <!-- 第二行 -->
            <div class="filter-row region-row">
              <!-- 区域添加 -->
              <div class="form-group">
                <div class="input-group">
                  <select v-model="regionCompare.regionToAdd" class="form-select region-select">
                    <option value="">选择区域</option>
                    <option v-for="province in provinces" 
                            :key="province" 
                            :value="province">
                      {{ province }}
                    </option>
                  </select>
                  <button @click="addRegion" 
                          class="btn btn-primary"
                          :disabled="!canAddRegion">
                    <i class="fas fa-plus"></i>
                    添加区域
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 已选区域标签 -->
          <div class="selected-items">
            <div v-for="region in selectedRegions" 
                 :key="region" 
                 class="item-tag">
              <span class="tag-text">{{ region }}</span>
              <button class="remove-btn" @click="removeRegion(region)">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <!-- 图表展示区域 -->
          <div class="chart-wrapper">
            <div v-if="loading" class="chart-loading">
              <div class="loading-spinner"></div>
              <span>加载中...</span>
            </div>
            <div ref="regionChart" class="chart" :class="{ 'is-loading': loading }"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import PageHeader from '@/components/PageHeader.vue'
import * as echarts from 'echarts'
import allResults from '@/utils/all_results.json'

export default {
  name: 'CompareView',
  components: {
    PageHeader
  },
  setup() {
    const store = useStore()
    
    // 保留年份列表用于X轴
    const years = ['2020', '2025', '2030', '2035', '2040', '2045', '2050', '2055', '2060']
    
    // 从状态中移除年份
    const regionCompare = ref({
      selectedScenario: '',
      selectedVariable: '',
      regionToAdd: '',
      chartType: 'line',
      selectedSector: ''
    })

    // 已选择的区域
    const selectedRegions = ref([])

    // 图表引用
    const regionChart = ref(null)
    let regionChartInstance = null

    // 省份列表
    const provinces = [
      '北京', '天津', '河北', '山西', '内蒙古', '辽宁', '吉林', '黑龙江',
      '上海', '江苏', '浙江', '安徽', '福建', '江西', '山东', '河南',
      '湖北', '湖南', '广东', '广西', '海南', '重庆', '四川', '贵州',
      '云南',  '陕西', '甘肃', '青海', '宁夏', '新疆'
    ]

    // 获取自定义情景列表
    const customScenarios = computed(() => {
      const scenarios = store.state.scenarios
      if (!scenarios) return []
      return Object.keys(scenarios).filter(s => s !== 'baseline')
    })

    // 判断是否可以添加区域
    const canAddRegion = computed(() => {
      return regionCompare.value.selectedScenario && 
             regionCompare.value.regionToAdd && 
             !selectedRegions.value.includes(regionCompare.value.regionToAdd)
    })

    // 添加区域
    function addRegion() {
      if (canAddRegion.value) {
        selectedRegions.value.push(regionCompare.value.regionToAdd)
        regionCompare.value.regionToAdd = ''
        updateRegionChart()
      }
    }

    // 移除区域
    function removeRegion(region) {
      selectedRegions.value = selectedRegions.value.filter(r => r !== region)
      updateRegionChart()
    }

    // 获取图表数据
    const getChartData = (scenarios, scope, province, analysisType, sector) => {
      const years = ['2020', '2025', '2030', '2035', '2040', '2045', '2050', '2055', '2060']
      
      return scenarios.map(scenario => {
        const scenarioData = store.state.scenarios[scenario]
        if (!scenarioData) return null
        
        return {
          name: scenario === 'baseline' ? '碳中和情景' : scenario,
          data: years.map(year => {
            switch(regionCompare.value.selectedVariable) {
              case 'gdp':
                return scenarioData[province].gdp[year] || 0
              case 'pop':
                return scenarioData[province].pop[year] || 0
              case 'intensity':
                if (regionCompare.value.selectedSector) {
                  return scenarioData[province].sectors[regionCompare.value.selectedSector].intensity[year] || 0
                }
                return 0
              case 'electrification':
                if (regionCompare.value.selectedSector) {
                  return scenarioData[province].sectors[regionCompare.value.selectedSector].electrification[year] || 0
                }
                return 0
              case 'hydrogenation':
                if (regionCompare.value.selectedSector) {
                  return scenarioData[province].sectors[regionCompare.value.selectedSector].hydrogenation[year] || 0
                }
                return 0
              default:
                return 0
            }
          })
        }
      }).filter(Boolean)
    }

    // 获取变量单位
    function getVariableUnit(variable) {
      const units = {
        gdp: '亿元',
        pop: '万人',
        intensity: '吨标准煤/亿元',
        electrification: '%',
        hydrogenation: '%'
      }
      return units[variable] || ''
    }

    // 获取变量名称
    function getVariableName(variable) {
      const names = {
        gdp: 'GDP',
        pop: '人口',
        intensity: '能源强度',
        electrification: '电气化率',
        hydrogenation: '氢能化率'
      }
      return names[variable] || ''
    }

    // 获取部门名称的函数
    function getSectorName(sector) {
      const names = {
        industry: '工业',
        construction: '建筑',
        agriculture: '农业',
        transport: '交通',
        service: '服务业',
        residential: '居民',
        others: '其他'
      }
      return names[sector] || ''
    }

    // 更新区域对比图表
    function updateRegionChart() {
      if (!regionChartInstance || 
          !regionCompare.value.selectedScenario || 
          !regionCompare.value.selectedVariable || 
          !selectedRegions.value.length) return

      // 通用的字体大小配置
      const fontConfig = {
        titleSize: 22,
        legendSize: 22,
        axisLabelSize: 22,
        axisNameSize: 20,
        tooltipSize: 20
      }

      if (regionCompare.value.selectedVariable === 'energy') {
        const fuelTypes = [
          { name: '电', key: 'electric', color: '#91cc75' },
          { name: '氢', key: 'hydrogen', color: '#5470c6' },
          { name: '煤', key: 'coal', color: '#333333' },
          { name: '油', key: 'oil', color: '#ee6666' },
          { name: '气', key: 'gas', color: '#73c0de' }
        ]

        const series = []
        const barWidth = Math.max(10, Math.min(30, 100 / selectedRegions.value.length)) + '%'
        const barGap = Math.max(5, Math.min(30, 50 / selectedRegions.value.length)) + '%'

        selectedRegions.value.forEach(region => {
          fuelTypes.forEach(fuelType => {
            series.push({
              name: fuelType.name,
              type: 'bar',
              stack: region,  // 使用省份名作为堆叠的标识
              emphasis: { focus: 'series' },
              itemStyle: { color: fuelType.color },
              barWidth: barWidth,  // 动态设置柱形宽度
              barGap: barGap,     // 动态设置柱形间距
              data: years.map(year => {
                let value = 0
                if (regionCompare.value.selectedSector === 'all' || !regionCompare.value.selectedSector) {
                  // 如果是全部门或未选择部门，累加所有部门的数据
                  Object.keys(allResults[region]).forEach(sector => {
                    const yearData = allResults[region][sector].find(d => d.year === year)
                    if (yearData) {
                      value += yearData[fuelType.key] / 10000  // 转换为万吨标煤
                    }
                  })
                } else {
                  // 如果是特定部门，只取该部门的数据
                  const yearData = allResults[region][regionCompare.value.selectedSector]?.find(d => d.year === year)
                  if (yearData) {
                    value += yearData[fuelType.key] / 10000  // 转换为万吨标煤
                  }
                }
                return value
              })
            })
          })
        })

        // 修改图表配置
        const option = {
          title: {
            text: `${regionCompare.value.selectedScenario === 'baseline' ? '碳中和情景' : regionCompare.value.selectedScenario}：${
              regionCompare.value.selectedVariable === 'energy' ?
                (regionCompare.value.selectedSector === 'all' || !regionCompare.value.selectedSector ? 
                  '全部门终端用能' :
                  `${getSectorName(regionCompare.value.selectedSector)}部门终端用能`) :
                ['gdp', 'pop'].includes(regionCompare.value.selectedVariable) ?
                  getVariableName(regionCompare.value.selectedVariable) :
                  `${getSectorName(regionCompare.value.selectedSector)}部门${getVariableName(regionCompare.value.selectedVariable)}`
            }对比`,
            left: 'center',
            textStyle: {
              fontSize: fontConfig.titleSize,
              fontWeight: 'bold'
            }
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: function(params) {
              const year = params[0].axisValue
              let result = `<div style="font-size:${fontConfig.tooltipSize}px;font-weight:bold;margin-bottom:10px">${year}年</div>`
              
              // 按省份分组显示数据
              const regionGroups = {}
              params.forEach(param => {
                const region = param.seriesName.split('-')[0]
                if (!regionGroups[region]) {
                  regionGroups[region] = {
                    total: 0,
                    fuels: []
                  }
                }
                regionGroups[region].fuels.push({
                  name: param.seriesName,
                  value: param.value
                })
                regionGroups[region].total += param.value
              })
              
              // 显示每个省份的燃料构成
              Object.entries(regionGroups).forEach(([region, data]) => {
                result += `<div style="margin-top:12px;font-weight:bold;font-size:${fontConfig.axisLabelSize}px">${region}</div>`
                data.fuels.forEach(fuel => {
                  const percentage = data.total > 0 ? ((fuel.value / data.total) * 100).toFixed(1) : '0.0'
                  result += `<div style="display:flex;justify-content:space-between;margin:6px 0;font-size:${fontConfig.axisLabelSize}px">
                    <span>${fuel.name}:</span>
                    <span>${fuel.value.toFixed(2)}万吨标准煤 (${percentage}%)</span>
                  </div>`
                })
                result += `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #eee;font-size:${fontConfig.axisLabelSize}px">
                  <span>总计:</span>
                  <span style="font-weight:bold">${data.total.toFixed(2)}万吨标准煤</span>
                </div>`
              })
              
              return result
            }
          },
          legend: {
            data: fuelTypes.map(f => f.name),
            bottom: 10,
            left: 'center',
            textStyle: {
              fontSize: fontConfig.legendSize
            }
          },
          grid: {
            left: '5%',
            right: '5%',
            bottom: '15%',
            top: '10%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: years,
            name: '',
            axisLabel: {
              interval: 0,
              fontSize: fontConfig.axisLabelSize
            },
            nameTextStyle: {
              fontSize: fontConfig.axisNameSize,
              padding: [10, 0, 0, 0]
            }
          },
          yAxis: {
            type: 'value',
            name: getYAxisName(),
            nameTextStyle: {
              fontSize: 20,
              padding: [0, 0, 0, 50]
            },
            axisLabel: {
              fontSize: 20
            }
          },
          series: series.map(s => ({
            ...s,
            barWidth: barWidth,
            barGap: barGap,
            label: {
              show: false,
              position: 'top',
              fontSize: fontConfig.axisLabelSize
            }
          }))
        }

        // 添加自适应布局配置
        option.grid = {
          left: '5%',
          right: '5%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        }

        regionChartInstance.setOption(option, true)
      } else {
        const seriesData = selectedRegions.value.map(region => {
          const scenarioData = store.state.scenarios[regionCompare.value.selectedScenario]
          if (!scenarioData || !scenarioData[region]) return {
            name: region,
            data: ['2020', '2025', '2030', '2035', '2040', '2045', '2050', '2055', '2060'].map(() => 0)
          }

          return {
            name: region,
            data: ['2020', '2025', '2030', '2035', '2040', '2045', '2050', '2055', '2060'].map(year => {
              switch(regionCompare.value.selectedVariable) {
                case 'gdp':
                  return scenarioData[region].gdp[year] || 0
                case 'pop':
                  return scenarioData[region].pop[year] || 0
                case 'intensity':
                  if (regionCompare.value.selectedSector) {
                    return scenarioData[region].sectors[regionCompare.value.selectedSector].intensity[year] || 0
                  }
                  return 0
                case 'electrification':
                  if (regionCompare.value.selectedSector) {
                    return scenarioData[region].sectors[regionCompare.value.selectedSector].electrification[year] || 0
                  }
                  return 0
                case 'hydrogenation':
                  if (regionCompare.value.selectedSector) {
                    return scenarioData[region].sectors[regionCompare.value.selectedSector].hydrogenation[year] || 0
                  }
                  return 0
                default:
                  return 0
              }
            })
          }
        })

        const option = {
          title: {
            text: `${regionCompare.value.selectedScenario === 'baseline' ? '碳中和情景' : regionCompare.value.selectedScenario}：${
              ['gdp', 'pop'].includes(regionCompare.value.selectedVariable) ?
                getVariableName(regionCompare.value.selectedVariable) :
                `${getSectorName(regionCompare.value.selectedSector)}部门${getVariableName(regionCompare.value.selectedVariable)}`
            }对比`,
            left: 'center',
            textStyle: {
              fontSize: fontConfig.titleSize,
              fontWeight: 'bold'
            }
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: regionCompare.value.chartType === 'line' ? 'line' : 'shadow'
            },
            formatter: function(params) {
              const year = params[0].axisValue
              let result = `<div style="font-size:${fontConfig.tooltipSize}px;font-weight:bold;margin-bottom:10px">${year}年</div>`
              params.forEach(param => {
                result += `<div style="display:flex;justify-content:space-between;font-size:${fontConfig.tooltipSize}px">
                  <span>${param.seriesName}:</span>
                  <span>${param.value}${getVariableUnit(regionCompare.value.selectedVariable)}</span>
                </div>`
              })
              return result
            }
          },
          legend: {
            data: seriesData.map(item => item.name),
            bottom: 10,
            left: 'center',
            textStyle: {
              fontSize: fontConfig.legendSize
            }
          },
          grid: {
            left: '5%',
            right: '5%',
            bottom: '15%',
            top: '15%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: ['2020', '2025', '2030', '2035', '2040', '2045', '2050', '2055', '2060'],
            name: '',
            axisLabel: {
              fontSize: fontConfig.axisLabelSize
            },
            nameTextStyle: {
              fontSize: fontConfig.axisNameSize,
              padding: [10, 0, 0, 0]
            }
          },
          yAxis: {
            type: 'value',
            name: getYAxisName(),
            nameTextStyle: {
              fontSize: 20,
              padding: [0, 0, 0, 50]
            },
            axisLabel: {
              fontSize: 20
            }
          },
          series: seriesData.map(item => ({
            name: item.name,
            type: regionCompare.value.chartType,
            data: item.data,
            smooth: regionCompare.value.chartType === 'line',
            symbol: regionCompare.value.chartType === 'line' ? 'circle' : 'none',
            symbolSize: 6,
            barMaxWidth: 50,
            label: {
              show: false,
              position: 'top',
              fontSize: fontConfig.axisLabelSize
            }
          }))
        }

        regionChartInstance.setOption(option, true)
      }
    }

    // 监听变量变化，更新对应的图表
    watch([
      () => regionCompare.value.selectedVariable,
      () => regionCompare.value.selectedScenario,
      () => regionCompare.value.selectedSector,
      () => selectedRegions.value,
      () => regionCompare.value.chartType
    ], () => {
      nextTick(() => {
        updateRegionChart()
      })
    }, { deep: true })

    // 组件挂载时初始化图表
    onMounted(() => {
      nextTick(() => {
        if (regionChart.value) {
          regionChartInstance = echarts.init(regionChart.value)
        }

        // 添加窗口大小变化的监听
        window.addEventListener('resize', () => {
          regionChartInstance?.resize()
        })
      })
    })

    // 辅助函数：获取部门能耗
    function getSectorEnergy(data, sector, year) {
      switch(sector) {
        case 'industry':
        case 'construction':
          return (data.gdp_composition.secondary[year] || 0) * 
                 (data.sectors[sector].intensity[year] || 0);
        case 'agriculture':
          return (data.gdp_composition.primary[year] || 0) * 
                 (data.sectors[sector].intensity[year] || 0);
        case 'service':
        case 'others':
          return (data.gdp_composition.tertiary[year] || 0) * 
                 (data.sectors[sector].intensity[year] || 0);
        case 'transport':
          return (data.gdp[year] || 0) * 
                 (data.sectors[sector].intensity[year] || 0);
        case 'residential':
          return (data.pop[year] || 0) * 
                 (data.sectors[sector].intensity[year] || 0);
        default:
          return 0;
      }
    }

    // 在 setup() 中添加计算属性
    const showSectorSelect = computed(() => {
      return ['intensity', 'electrification', 'hydrogenation', 'energy'].includes(regionCompare.value.selectedVariable)
    })

    // 添加部门选项计算属性
    const sectorOptions = computed(() => {
      const variable = regionCompare.value.selectedVariable
      
      // 基础部门列表
      const baseSectors = [
        { value: '', label: '选择部门' },
        { value: 'agriculture', label: '农业' },
        { value: 'industry', label: '工业' },
        { value: 'construction', label: '建筑业' },
        { value: 'transport', label: '交通运输' },
        { value: 'service', label: '服务业' },
        { value: 'residential', label: '居民生活' }
      ]
      
      // 只有终端用能变量时才显示"全部门"选项
      if (variable === 'energy') {
        return [
          { value: '', label: '选择部门' },
          { value: 'all', label: '全部门' },
          ...baseSectors.slice(1)
        ]
      }
      
      return baseSectors
    })

    // 添加 watch 来强制设置图表类型
    watch(() => regionCompare.value.selectedVariable, (newVal) => {
      if (newVal === 'energy') {
        regionCompare.value.chartType = 'bar'
      }
    })

    // 添加计算部门能耗的辅助函数
    const calculateSectorEnergy = (scenarioData, province, sector, year) => {
      try {
        const data = scenarioData[province]
        if (!data) {
          console.error(`No data for province: ${province}`)
          return 0
        }

        let energy = 0
        switch(sector) {
          case 'agriculture':
            energy = data.gdp_composition?.primary?.[year] * data.sectors?.agriculture?.intensity?.[year]
            break
          case 'industry':
            energy = data.gdp_composition?.secondary?.[year] * data.sectors?.industry?.intensity?.[year]
            break
          case 'construction':
            energy = data.gdp_composition?.secondary?.[year] * data.sectors?.construction?.intensity?.[year]
            break
          case 'transport':
            energy = data.gdp?.[year] * data.sectors?.transport?.intensity?.[year]
            break
          case 'service':
            energy = data.gdp_composition?.tertiary?.[year] * data.sectors?.service?.intensity?.[year]
            break
          case 'residential':
            energy = data.pop?.[year] * data.sectors?.residential?.intensity?.[year]
            break
          case 'others':
            energy = data.gdp_composition?.tertiary?.[year] * data.sectors?.others?.intensity?.[year]
            break
        }
        return energy || 0
      } catch (error) {
        console.error('Error calculating sector energy:', error)
        return 0
      }
    }

    // 在 script 中添加 watch
    watch(() => regionCompare.value.selectedSector, (newVal) => {
      // 当选择全部门时，强制变量为终端用能
      if (newVal === 'all') {
        regionCompare.value.selectedVariable = 'energy'
      }
    }, { immediate: true })

    // 修改获取y轴名称的函数
    function getYAxisName() {
      switch(regionCompare.value.selectedVariable) {
        case 'gdp':
          return '(亿元)'
        case 'pop':
          return '(万人)'
        case 'intensity':
          // 根据不同部门返回不同的单位
          switch(regionCompare.value.selectedSector) {
            case 'transport':
              return '(吨标煤/亿吨公里)'
            case 'residential':
              return '(吨标煤/万人)'
            default:
              return '(吨标煤/亿元)'
          }
        case 'electrification':
        case 'hydrogenation':
          return '(%)'
        case 'energy':
          return '(万吨标煤)                 '
        default:
          return ''
      }
    }

    return {
      years,
      regionCompare,
      selectedRegions,
      provinces,
      customScenarios,
      canAddRegion,
      addRegion,
      removeRegion,
      regionChart,
      showSectorSelect,
      sectorOptions,
      calculateSectorEnergy
    }
  }
}
</script>

<style scoped>
/* 基础布局样式 */
.compare-view {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  animation: fadeIn 0.6s ease-out;
}

/* 卡片基础样式 */
.card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: linear-gradient(to right, #f8f9fa, #ffffff);
  border-bottom: 1px solid #eef2f7;
}

.card-header i {
  font-size: 32px;
  color: #1890ff;
}

.card-header h3 {
  font-size: 28px;
  color: #2d3748;
  font-weight: 600;
  margin: 0;
}

.card-body {
  padding: 30px;
}

/* 表单元素样式 */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 10px;
  font-size: 20px;
  font-weight: 500;
  color: #1e4c6b;
}

.form-select {
  width: 100%;
  padding: 12px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 22px;
  color: #4a5568;
  background-color: white;
  transition: all 0.2s ease;
}

.form-select:hover {
  border-color: #40a9ff;
}

.form-select:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* 筛选区域布局 */
.filter-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
}

/* 输入组合样式 */
.input-group {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-start;
}

.input-group .form-select {
  flex: 1;
}

/* 按钮样式 */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 24px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  height: 48px;
}

.btn i {
  font-size: 22px;
}

.btn-primary {
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #40a9ff, #1890ff);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background: linear-gradient(135deg, #d9d9d9, #bfbfbf);
  cursor: not-allowed;
  transform: none;
}

/* 已选标签样式 */
.selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 15px;
  min-height: 50px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 8px;
}

.item-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: white;
  border: 1px solid #e6f7ff;
  border-radius: 6px;
  font-size: 20px;
  color: #1890ff;
  box-shadow: 0 2px 6px rgba(24, 144, 255, 0.1);
  animation: fadeIn 0.3s ease-out;
  transition: all 0.3s ease;
}

.item-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
}

.tag-text {
  margin-right: 8px;
}

.remove-btn {
  background: none;
  border: none;
  padding: 2px 6px;
  color: #999;
  cursor: pointer;
  transition: color 0.3s;
  border-radius: 4px;
}

.remove-btn:hover {
  color: #ff4d4f;
  background: rgba(255, 77, 79, 0.1);
}

/* 图表容器样式 */
.chart-wrapper {
  position: relative;
  margin-top: 25px;
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.chart {
  height: 600px;
  width: 100%;
}

.chart-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.chart-wrapper:hover::before {
  opacity: 1;
}

/* 加载状态样式 */
.chart-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 1;
}

.loading-spinner {
  display: inline-block;
  width: 30px;
  height: 30px;
  border: 2px solid #1890ff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

.chart.is-loading {
  opacity: 0.5;
  filter: blur(2px);
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .container {
    padding: 15px;
  }
  
  .filter-section {
    grid-template-columns: 1fr;
  }
  
  .input-group {
    flex-direction: column;
  }
  
  .chart {
    height: 400px;
  }
  
  .card-header h3 {
    font-size: 24px;
  }
  
  .form-select, .btn {
    font-size: 18px;
  }
}

/* 新增样式 */
.filter-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 25px;
  margin-bottom: 20px;
}

/* 区域选择行样式 */
.region-row {
  grid-template-columns: 1fr !important;
}

/* 区域选择框样式 */
.region-select {
  max-width: 200px;
}

/* 调整按钮和选择框的对齐 */
.input-group {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-start;
}
</style> 